import {
  Email,
  Lock,
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppLogo from "../components/AppLogo.jsx";
import LanguageSwitcher from "../components/LanguageSwitcher.jsx";
import {
  AUTH_ERROR_STORAGE_KEY,
  getAuthErrorMessage,
} from "../utils/authErrors";
import { clearError, loginUser } from "../store/slices/authSlice";

const LoginPage = ({ onLoginSuccess }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading, error, user } = useSelector(
    (state) => state.auth
  );

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [sessionError, setSessionError] = useState(null);

  const authErrorCode = sessionError || error;
  const authErrorMessage = authErrorCode
    ? getAuthErrorMessage(authErrorCode, t)
    : null;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && onLoginSuccess) {
      onLoginSuccess();
    }
  }, [isAuthenticated, user, onLoginSuccess]);

  useEffect(() => {
    const storedErrorCode = sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
    if (storedErrorCode) {
      setSessionError(storedErrorCode);
      sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
    }
  }, []);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = t("login.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t("login.emailInvalid");
    }

    if (!formData.password) {
      errors.password = t("login.passwordRequired");
    } else if (formData.password.length < 6) {
      errors.password = t("login.passwordMinLength");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      dispatch(loginUser(formData)).then((result) => {
        if (result.type === "auth/loginUser/fulfilled" && onLoginSuccess) {
          onLoginSuccess();
        }
      });
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ position: "absolute", top: 16, right: 16 }}>
          <LanguageSwitcher />
        </Box>
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <AppLogo height={90} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t("login.subtitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("login.title")}
            </Typography>
          </Box>

          {authErrorMessage && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                textAlign: i18n.language === "ar" ? "right" : "left",
              }}
            >
              {authErrorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t("common.email")}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              disabled={isLoading}
              placeholder={t("login.emailPlaceholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t("common.password")}
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              disabled={isLoading}
              placeholder={t("login.passwordPlaceholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword
                          ? t("login.hidePassword")
                          : t("login.showPassword")
                      }
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <LoginIcon />
              }
              sx={{
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: "bold",
              }}
            >
              {isLoading ? t("common.loading") : t("login.loginButton")}
            </Button>
          </Box>

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              نظام إدارة التذاكر - Customer Care System
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
